import { getActiveUiConfig } from '@/config/uiConfig'
import { EpicAuthClient } from '@/features/auth/services/EpicAuthClient'
import { clearDiscardMarker } from '@/features/ordering/discardMarker'
import { resolvePatientIdForDraftLookup } from '@/utils/orderDraftPatientId'
import type { OrderFormInfoData } from '@/types/RequisitionFormTypes'
import { selectFinalGuidanceReport } from '@/features/report/selectFinalGuidanceReport'
import { getAllOrderReports } from '@/features/report/services/reportClient'
import { legacyTrfPayloadToCanonical } from '@/ordering/canonicalOrder'
import { useAppDispatch } from '@/store/hooks'
import { setCanonicalOrder } from '@/store/slices/canonicalOrderSlice'
import {
  getOrderByPatient,
  getOrderStatusByPatient,
  OrderStatus,
  OrderStatusResponse,
} from '@/utils/DatabaseOperations'
import * as Sentry from '@sentry/react'
import React, { useCallback, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

interface AccessContext {
  fhirPatientId: string
  userName: string
  userLastName: string
}

export interface CallbackConfig {
  /**
   * Retrieves the access context using the authorization code and state
   * @param authCode - The authorization code from the OAuth callback
   * @param state - The state parameter for session validation
   * @returns Promise with access context containing fhirPatientId
   */
  retrieveAccessContext: (authCode: string, state: string) => Promise<AccessContext>

  /**
   * Optional custom loading message
   */
  loadingMessage?: string
}

export interface BaseCallbackProps {
  config: CallbackConfig
}

/**
 * Base SMART on FHIR Callback component that handles access token request
 * This component can be extended for specific implementations (e.g., epic-sandbox)
 */
const BaseCallback: React.FC<BaseCallbackProps> = ({ config }) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const searchParams = useSearchParams().at(0) as URLSearchParams
  const authCode = searchParams.get('code') || ''
  const state = searchParams.get('state') || ''

  const SmartOnFHIRAccess = useCallback(async () => {
    // Canonical epicState shape used for all navigate calls from this component.
    const epicNav = { epicState: state }
    try {
      const accessContext = await config.retrieveAccessContext(authCode, state)

      // A fresh Epic SMART launch is a deliberate re-entry, so it always clears any
      // discard marker for the launch patient. This runs before the routing branches
      // below because the post-discard launch has no order to return: the SPA
      // substitutes a synthetic DRAFT carrying no `json_payload`, so anything gated on
      // the payload would skip exactly the case that needs clearing. The launch context
      // alone resolves the id. A refresh of /order/start never re-enters this component,
      // so marker suppression still survives a reload (GEN-3272 R8).
      const launchLookupId = resolvePatientIdForDraftLookup({
        launchMode: 'epic_smart',
        orderFormInfoData: {} as OrderFormInfoData,
        epicOAuthState: state,
      })
      if (launchLookupId) {
        clearDiscardMarker(launchLookupId)
      }

      const caller = `${accessContext.userName || ''} ${accessContext.userLastName || ''}`.trim() || 'spa'
      // Epic access token for the active session (if any). When present, both
      // the status and full-order lookups resolve the order by FHIR-derived MRN
      // + the caller's org, treating fhirPatientId as the Epic FHIR Patient
      // logical id. They fall back to the proband lookup if the token / FHIR
      // read / MRN lookup yields nothing, so this is safe to pass
      // unconditionally. Both calls must receive the same token so the routing
      // branch (driven by existingRequest.status) agrees with orderStatusResponse.
      let epicAccessToken: string | null = null
      try {
        epicAccessToken = EpicAuthClient.getInstance().getAccessToken(state)
      } catch {
        epicAccessToken = null
      }
      const orderStatusResponse: OrderStatusResponse | null = await getOrderStatusByPatient(
        accessContext.fhirPatientId,
        caller,
        epicAccessToken
      )
      const existingRequest = await getOrderByPatient(accessContext.fhirPatientId, caller, epicAccessToken)

      // Determine if order is still in editing phase (DRAFT)
      const isEditingPhase = existingRequest.status === OrderStatus.DRAFT
      const isResultReady = orderStatusResponse?.status === OrderStatus.COMPLETE
      const orderId = orderStatusResponse?.order_id || existingRequest.id

      if (isEditingPhase) {
        // Order is still being edited - navigate to new order start UI
        if (existingRequest.json_payload?.requisitionFormData) {
          dispatch(setCanonicalOrder(legacyTrfPayloadToCanonical({
            orderFormInfo: existingRequest.json_payload.orderFormInfo,
            requisitionFormData: existingRequest.json_payload.requisitionFormData,
          })))
        }
        navigate('/order/start', { state: epicNav })
      } else if (isResultReady && orderId) {
        // Order results are ready. Route to the Guidance page only when the org
        // has the guidance feature enabled AND this order has a finalized
        // guidance envelope (guidance.status === 'final'); otherwise fall back
        // to the PDF results page. Mirrors the Orders list routing so the Epic /
        // SMART-on-FHIR launch flow reaches guidance too.
        if (existingRequest.json_payload?.requisitionFormData) {
          dispatch(setCanonicalOrder(legacyTrfPayloadToCanonical({
            orderFormInfo: existingRequest.json_payload.orderFormInfo,
            requisitionFormData: existingRequest.json_payload.requisitionFormData,
          })))
        }
        // Read the freshly loaded config from the singleton rather than the
        // useUiConfig() hook: `retrieveAccessContext` above awaits
        // `loadEpicUiConfig`, which updates the active-config singleton, but the
        // React context (and any hook value captured in this closure) can still
        // reflect LOGIN_FALLBACK_UI_CONFIG (guidance: false) during this async
        // callback.
        let destination = `/order/results/${encodeURIComponent(orderId)}`
        if (getActiveUiConfig()?.featureFlags?.guidance === true) {
          try {
            const reports = await getAllOrderReports(orderId, caller)
            if (selectFinalGuidanceReport(reports)) {
              destination = `/order/guidance/${encodeURIComponent(orderId)}`
            }
          } catch (error) {
            Sentry.captureException(error, { tags: { operation: 'callback.guidance_check', orderId } })
          }
        }
        navigate(destination, {
          state: {
            epicState: state,
            orderId,
            orderData: existingRequest.json_payload?.orderFormInfo
              ? {
                  ...existingRequest.json_payload.orderFormInfo,
                  orderDate: existingRequest.created_at ? new Date(existingRequest.created_at) : undefined,
                  orderLastUpdateDate: existingRequest.updated_at ? new Date(existingRequest.updated_at) : undefined,
                }
              : undefined,
            backendStatus: orderStatusResponse?.status,
          },
        })
      } else {
        // Order has been submitted but not complete - navigate to in-progress
        if (existingRequest.json_payload?.requisitionFormData) {
          dispatch(setCanonicalOrder(legacyTrfPayloadToCanonical({
            orderFormInfo: existingRequest.json_payload.orderFormInfo,
            requisitionFormData: existingRequest.json_payload.requisitionFormData,
          })))
        }
        const orderId = orderStatusResponse?.order_id || existingRequest.id
        const patientId = existingRequest.json_payload?.orderFormInfo?.patient?.id || existingRequest.patient_id
        navigate(`/order/in-progress/${orderId}/${patientId}`, {
          state: {
            epicState: state,
            orderId,
            orderData: existingRequest.json_payload?.orderFormInfo
              ? {
                  ...existingRequest.json_payload.orderFormInfo,
                  orderDate: existingRequest.created_at ? new Date(existingRequest.created_at) : undefined,
                  orderLastUpdateDate: existingRequest.updated_at ? new Date(existingRequest.updated_at) : undefined,
                }
              : undefined,
            backendStatus: orderStatusResponse?.status,
          },
        })
      }
    } catch (error) {
      console.error('Login error:', error)
      // On error, navigate to order start anyway to allow user to continue
      navigate('/order/start', { state: epicNav })
    }
  }, [authCode, state, navigate, dispatch, config])

  // Automatically trigger the access request when component mounts with proper validation
  useEffect(() => {
    // Only proceed if we have valid parameters and we're not already loading
    if (authCode && state) {
      const timer = setTimeout(() => {
        SmartOnFHIRAccess()
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, [SmartOnFHIRAccess, authCode, state])

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-md p-8">
        <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-lg">
          <div className="mt-8">
            <div className="text-center">
              <div
                className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                role="status"
              >
                <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                  Loading...
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-600">{config.loadingMessage || 'Requesting access context...'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BaseCallback
